import { supabase } from '../supabaseClient'
import { readFunctionError } from '../lib/functionError'

// True "log in as this student" for staff. Unlike the read-only view-as mirror,
// this swaps the browser's Supabase session to a REAL student session (minted
// server-side by the impersonate-student edge function) so the app behaves
// exactly as it does for the student — full navigation, real writes, student
// RLS. Used during live sessions to teach a feature hands-on or to reproduce a
// reported bug faithfully.
//
// The staffer's own session is stashed in localStorage before the swap and
// restored on exit. localStorage (not sessionStorage) so a mid-session reload
// or crash still leaves a way back to the staff account. Because the whole app
// shares one Supabase session per browser origin, only one window should be
// used while impersonating — other tabs will follow the swap.

const STAFF_BACKUP_KEY = 'scholar.impersonation.staffSession'
const FLAG_KEY         = 'scholar.impersonation.active'
const CHANGE_EVENT     = 'impersonation-change'

export interface ImpersonationFlag {
  studentId:   string
  studentName: string
}

export function getImpersonation(): ImpersonationFlag | null {
  try {
    const raw = localStorage.getItem(FLAG_KEY)
    return raw ? (JSON.parse(raw) as ImpersonationFlag) : null
  } catch {
    return null
  }
}

export function isImpersonating(): boolean {
  return getImpersonation() !== null
}

// Subscribe to start/exit (this tab) and cross-tab storage changes.
export function onImpersonationChange(cb: () => void): () => void {
  window.addEventListener(CHANGE_EVENT, cb)
  window.addEventListener('storage', cb)
  return () => {
    window.removeEventListener(CHANGE_EVENT, cb)
    window.removeEventListener('storage', cb)
  }
}

export async function startImpersonation(studentId: string, studentName: string): Promise<void> {
  // Capture the staff session first — refuse if there's nothing to return to.
  const { data: { session: staff } } = await supabase.auth.getSession()
  if (!staff?.refresh_token) throw new Error('No active session to return to. Please sign in again.')

  // Mint a real student session token server-side (staff-checked + audited).
  const { data, error } = await supabase.functions.invoke('impersonate-student', {
    body: { student_id: studentId },
  })
  if (error) throw new Error(await readFunctionError(error))
  if (data?.error) throw new Error(data.error)
  const tokenHash = data?.token_hash as string | undefined
  if (!tokenHash) throw new Error('Could not start — no session token returned.')

  // Stash the staff session only once we know the mint succeeded.
  localStorage.setItem(STAFF_BACKUP_KEY, JSON.stringify({
    access_token:  staff.access_token,
    refresh_token: staff.refresh_token,
  }))

  // Swap into the student session.
  const { error: vErr } = await supabase.auth.verifyOtp({ token_hash: tokenHash, type: 'magiclink' })
  if (vErr) {
    localStorage.removeItem(STAFF_BACKUP_KEY)
    throw new Error(vErr.message || 'Could not switch into the student session.')
  }

  localStorage.setItem(FLAG_KEY, JSON.stringify({ studentId, studentName }))
  window.dispatchEvent(new Event(CHANGE_EVENT))
}

export async function exitImpersonation(): Promise<void> {
  const raw = localStorage.getItem(STAFF_BACKUP_KEY)
  localStorage.removeItem(FLAG_KEY)
  localStorage.removeItem(STAFF_BACKUP_KEY)
  window.dispatchEvent(new Event(CHANGE_EVENT))

  if (raw) {
    try {
      const { access_token, refresh_token } = JSON.parse(raw) as { access_token: string; refresh_token: string }
      await supabase.auth.setSession({ access_token, refresh_token })
      return
    } catch {
      // Fall through to a clean sign-out if the backup is unusable.
    }
  }
  await supabase.auth.signOut()
}
